import type {
  BaseInfinityScrollList,
  BooleanYn,
  CommunityUserType,
  UserCodeType,
  CreatorGrade,
  CreatorOrganizationCode,
  StplatGroupTyCode,
  PayType,
  BasePaginationList,
  BlockStatusModel,
  CreatorRegStatus,
  DaoApplyStatus,
  CreatorDAOSttusCode,
  Nullable,
} from '@store/types'
import type {
  RecommendUser,
  NttTypeCode,
  PostsItem,
  CommentAndReply,
  CommentsAndRepliesPayload,
  EditorImage,
} from '@store/post/type'
import type { RewardItem, RewardCount } from '@store/mypage/type'

export interface CreatorStatItem {
  statRdVal: number // 조회수
  statRdDiff: number // 조회수 백분율
  statFwVal: number // 팔로워수
  statFwDiff: number // 팔로워 백분율
  statRankVal: number // 랭킹
  statRankDiff: number // 랭킹 백분율
  statRewardVal: number // 보상
  statRewardDiff: number // 보상 백분율
  shareTotalCount: number // 전체 공유 수
  shareNormalCount: number // 일반게시형 포스팅 공유 수
  shareCardCount: number // 카드형 포스팅 공유 수
  shareVideoCount: number // 영상형 포시팅 공유 수
}

/**
 * 크리에이터 신청 요건 타입
 */
export type ApplyCreatorConditionType =
  | 'CUAR' // 대표 콘텐츠 3개 이상 제출
  | 'CUFW' // 팔로워 수

/**
 * 크리에이터 콘텐츠 정렬
 */
export type CreatorContentsOrderBy =
  | 'newest' // 최신순
  | 'oldest' // 과거순
  | 'hot' // 인기순

export type DaoCreatorsFilter =
  | 'all' // 전체
  | 'dao' // dao 개설 크리에이터
/**
 * 크리에이터 SNS 코드
 */
export type CreatorSnsCode =
  | 'HMPG' // unknown(homepage)
  | 'YUTB' // youtube
  | 'INTA' // instagram
  | 'FCBK' // facebook
  | 'TWIR' // twitter

/**
 * 크리에이터 Sns Link
 */
export interface CreatorSocial {
  userOptionSn: number
  optionSeCode: string
  optionTyCode: CreatorSnsCode
  userSn: number | null
  optionCharstVal: string
  optionNumberVal: string | null
  optionNcm: string
  expsrOrdr: number
  deleteAt: string | null
  sysRegistDt: Date | null
  sysRegisterSn: string | null
  sysUpdtDt: Date | null
  sysUpdUserSn: string | null
  class: string
}

/**
 * 크리에이터 공지사항 (리스트 아이템과 상세가 같음)
 */
export interface CreatorNotice {
  crtrNttSn: number // 공지사항 일련번호
  crtrUserSn: number // 사용자 번호
  fixAt: string //
  prefixTyCode: string
  crtrNttSj: string // 공지사항 제목
  crtrNttCn: string // 공지사항 내용
  ntbdRdcnt: number //
  expsrOrdr: number //
  tmprNtbdAt: string //
  deleteAt: string // 삭제 여부
  sysRegistDt: string // 시스템 등록일시
  sysRegisterSn: string // 시스템 등록자 일련번호
  sysUpdUserSn: string // 시스템 수정자 일련번호
  sysUpdtDt: string // 시스템 수정일시
}

/**
 * 크리에이터 프로필 정보
 */
export interface CreatorProfile extends BlockStatusModel {
  acntTyCode: UserCodeType
  proflDc: string // 프로필 설명
  userNcnm: string // 사용자 닉네임
  userProflUrl: string // 프로필 사진 url
  userSn: number // 사용자 일련번호
  userAvataUseAt: BooleanYn // 아바타 세팅 유무
  recomendUserId: string | null // 추천인
  followerCount: number // 팔로우 수
  followingCount: number // 팔루잉 수
  followerYn: BooleanYn // 팔로우 여부
  rwardSmLM: string // 코인
  creatorLink: string // 크리에이터 별칭
  lastAt: string // 삭제 여부
  introduction: string // 소개 정보
  backgroundImage: string // 배경 이미지
  notices: CreatorNotice[] // 프로필에 노출 할 공지사항
  snslinks: CreatorSocial[] // 소셜 링크 목록
  crtrSpclGradCode: CreatorGrade // 크리에이터 등급
  crtrOrgnztTyCode: CreatorOrganizationCode // 크리에이터 조직 구분 코드
  prmbrshAt: BooleanYn // 크리에이터 프리미엄 개설 여부
  prmbrshSbscrbAt: BooleanYn // 프리미엄 가입 여부(조회 하는 사용자 개인화 정보)

  // 개인화 정보 (로그인한 사용자와 동일한 사용자 정보 조회 시에만 출력됨)
  bicBankNm: string // BIC 은행 명 – 요청 언어로 출력
  bicCode: string // BIC 코드
  bnkbDpstrNm: string // 통장 예금주 명
  bnkbAcnutno: string // 통장 계좌번호

  // 신규 모델
  blockedAt: BooleanYn // 다른 사용자가 현재 사용자 차단 여부 Y/N
  blockingAt: BooleanYn // 현재 사용자가 다른 사용자 차단 여부 Y/N
  followerAt: BooleanYn // 현재 사용자의 팔로워 여부 Y/N
  followingAt: BooleanYn // 다른 사용자가 다른 사용자 팔로우 여부 Y/N
  channelUrl: string | null
  createdOpenChannelAt: BooleanYn
  crtrStlcurFiatAt: BooleanYn | null // 크리에이터 정산통화 법정화폐 여부 Y/N
  crtrStlcurSymbolCodeNm: string | null // 크리에이터 정산통화 심볼 코드 명칭 (정산 방법 표준 코드 명)
  cxwaletAdres: string | null // 암호화폐지갑 주소
  liveOnAirAt: BooleanYn // 라이브 onAir 여부 Y/N
  sndbrdLiveSn: number | null // 라이브 일련번호
  mberKndDetailSeCodeNm: string | null // 회원 종류 상세 구분 코드 명칭 (사업자 종류 표준 코드 명)

  donationAvailableAt: BooleanYn | null // 후원 가능 여부

  // DAO 모델
  creatorDaoId: string | null // Creator DAO ID
  creatorDaoNm: string | null // Creator DAO 명
  creatorDaoSn: number | null
  creatorDaoOpenedAt: BooleanYn // Creator DAO 개설 여부 Y/N
  creatorDaoSttusCode: DaoApplyStatus | null // Creator DAO 상태 코드
}

/**
 * 크리에이터 프로필 조회 페이로드
 */
export interface CreatorProfilePayload {
  creatorUserSn: number // 크리이에터 사용자 번호
}

/**
 * 크리에이터 링크를 통한 프로필 조회 페이로드
 */
export interface CreatorProfileByCreatorLinkPayload {
  creatorLink: string // 크리이에터 링크
}

/**
 * 크리에이터 닉네임 변경
 */
export interface CreatorProfileEditPayload {
  proflDc: string //
  userNcnm: string //
  creatorLink: string // 크리에이터 별칭
}

/**
 * 크리에이터 콘텐츠 아이템
 */
export interface CreatorContentsItem extends PostsItem {}

export type CreatorContentsItemType =
  | 'content' // 콘텐츠
  | 'free' // 자유
  | 'notice' // 공지

/**
 * 크리에이터 콘텐츠 아이템 목록 페이로드
 */
export interface CreatorContentsItemListPayload {
  [Key: string]: any
  cmtyNttCtgrySn?: number // 카테고리 일련번호
  contentsType: CreatorContentsItemType // 콘텐츠 타입
  creatorUserSn?: number // 크리에이터 사용자 일련번호
  flwUserSn?: number // 팔로우 사용자 일련번호
  lastCmtyNttSn?: number // 마지막 게시물 일련번호
  lastSumSvcActScore?: number // 마지막 인기순 기준 점수 (인기순 정렬 필수)
  nextPageSize: number // 다음페이지 요청 개수
  orderBy: CreatorContentsOrderBy // 정렬 : newest(최신순), oldest(과거순), hot(인기순)
  pageNum: number // 페이지 번호
  pageSize: number // 한 페이지당 row 수
  searchHashtagNm?: string // 해시태그 검색 키워드
  searchNttSj?: string // 제목 검색 키워드
  srchPostType?: string // 검색 게시물 타입 (NORM: 일반형, CARD:카드형, CVOD:영상형)
}

/**
 * 크리에이터 콘텐츠 아이템 상세 페이로드
 */
export interface CreatorContentsItemPayload {
  cmtyNttSn: number
}

/**
 * 크리에이터 댓글 아이템
 */
export interface CreatorCommentItem {
  cmtyNttAnswerSn: number
  replyreAt: string
  nttParntsAnswerSn: number | null
  userSn: number
  userNcnm: string
  userProfl: string
  nttAnswerCn: string
  nttAnswerRegistDt: string
  recommendCount: number
  deleteAt: string
  sanctnsAt: string
  rcFlag: string
  dvFlag: string
  bkFlag: string
  mentnUserAt: string
  mentnUserSn: number | null
  mentnNickname: string | null
  recommendUserList: RecommendUser[]
}

/**
 * 크리에이터 댓글 아이템 페이로드
 */
export interface CreatorCommentItemListPayload {
  cmtyNttSn: number
  pageNum: number
  pageSize: number
  nextPageSize: number
  orderBy: CreatorContentsOrderBy
}

/**
 * 나의 댓글 목록 정렬
 */
export type CreatorCommentOrderBy =
  | 'newest' // 최신순
  | 'oldest' // 과거순
  | 'hot' // 인기순

/**
 * 보상탭 리스트 아이템 페이로드
 */
export interface RewardItemPayload {
  creatorUserSn: number
  pageNum: number
  pageSize: number
  nextPageSize: number
  orderBy: CreatorContentsOrderBy
}

export interface WeeklyRewardCount {
  totalReward: number
  unreadWeeklyEstimatedReward: number
  weeklyEstimatedReward: number
}

/**
 * 크리에이터 주간 예상 보상 내역 리워드 타입(FW: 팔로우, RC: 좋아요)
 */
export type WeeklyRewardsTyCode = 'FW' | 'RC'

export interface WeeklyRewardItem {
  svcActFrstRegistDt: string // 서비스 활동 최초 등록 일시
  svcActGroupClCode: WeeklyRewardsTyCode // 서비스 활동 분류 코드
  userNcnm: string // 사용자 닉네임
  weeklyEstimatedReward: string // 주간 예상 리워드
}
/**
 * 크리에이터 주간 예상 보상 내역 조회 페이로드
 */
export interface WeeklyRewardItemPayload {
  pageNum: number
  pageSize: number
  nextPageSize: number
  svcActGroupClCode: WeeklyRewardsTyCode
}

export interface WeeklyRewardItems {
  followCount: number // 팔로우 건수
  recommendCount: number // 좋아요 건수
  rewardDataList: WeeklyRewardItem[]
}

/**
 * 리워드 차트 정렬 조건
 */
export type RewardChartPeriod =
  | 'ALL' // 전체
  | 'MONTH' // 1개월
  | 'THREE_MONTHS' // 3개월

/**
 * 리워드 차트 종류
 */
export type RewardChartType =
  | 'REWARD' // 총 보상
  | 'CREATOR_ACTIVITY' // 크리에이터 활동(좋아요, 팔로우)

export interface RewardChartDataList {
  data: Array<string>
  label: WeeklyRewardsTyCode | 'ALL'
}
/**
 * 크리에이터 주간 예상 보상 내역 조회 페이로드
 */
export interface RewardChartPayload {
  chartPeriod: RewardChartPeriod
  chartTypeCode: RewardChartType
}

export interface RewardChartData {
  chartDataList: Array<RewardChartDataList>
  labels: Array<string>
}

export interface RewardChartActivityData {
  chartDataList: RewardChartDataList[]
  labels: Array<string>
}
/**
 * 보상 목록 정렬
 */
export type CreatorRewardsOrderBy =
  | 'newest' // 최신순
  | 'oldest' // 과거순
  | 'rewardQty' // 보상 크기순

/**
 * 크리에이터 링크 수정 페이로드: params
 */
export interface CreatorLinkUpdatePayload {
  creatorLinkNm: string
}

/**
 * 크리에이터 방명록 리스트 조회 페이로드
 */
export interface CreatorGuestBooksPayload {
  creatorUserSn: number // 크리에이터 사용자 일련번호
  lastGustSn?: number // 마지막 조회된 방명록 일련번호
  nextPageSize: number // 다음페이지 요청 개수
  pageNum: number // 페이지 번호
  pageSize: number // 한 페이지당 row 수
}

/**
 * 크리에이터 방명록 상세 조회 페이로드
 */
export interface CreatorGuestBookPayload {
  gustSn: number // 방명록 일련번호
}

/**
 * 크리에이터 방명록 (리스트 아이템과 상세가 같음)
 */
export interface CreatorGuestBook {
  cmtyUserSeCode: CommunityUserType // 크리에이터 사용자 구분코드(C: 크리에이터, G: 일반사용자)
  crtrSpclGradCode: CreatorGrade // 크리에이터 등급코드
  crtrOrgnztTyCode: CreatorOrganizationCode // 크리에이터 조직 구분 코드
  deleteAt: BooleanYn // 삭제여부
  gustCmtyUserSeCode: CommunityUserType // 크리에이터 사용자 구분코드(C: 크리에이터, G: 일반사용자)
  gustCn: string // 방명록 내용
  gustCrtrSpclGradCode: string // 방문 사용자 등급코드
  gustRdcnt: number // 방명록 조회수
  gustSn: number // 방명록 일련번호
  gustUserAvataUseAt: string // 방문 사용자 아바타 사용여부
  gustUserNcnm: string // 방문 사용자 닉네임
  gustUserProfl: string // 방문 사용자 프로필 URL
  gustUserSn: number // 방문 사용자 일련번호
  sysRegistDt: string // 시스템 등록일시
  sysRegisterSn: string // 시스템 등록자 일련번호
  sysUpdUserSn: string // 시스템 수정자 일련번호
  sysUpdtDt: string // 시스템 수정일시
  userAvataUseAt: string // 크리에이터 아바타 사용여부
  userNcnm: string // 크리에이터 닉네임
  userProfl: string // 크리에이터 프로필 URL
  userSn: 0 // 크리에이터 사용자 일련번호
}

/**
 * 크리에이터 방명록 등록 페이로드
 */
export interface RegCreatorGuestBookPayload {
  creatorUserSn: number // 크리에이터 사용자 일련번호
  gustCn: string // 방명록 내용
}

/**
 * 크리에이터 방명록 수정 페이로드
 */
export interface UpdateCreatorGuestBookPayload {
  creatorUserSn: number // 크리에이터 사용자 일련번호
  gustCn: string // 방명록 내용
  gustSn: number // 크리에이터 방명록 일련번호
}

/**
 * 크리에이터 방명록 삭제 페이로드
 */
export interface DeleteCreatorGuestBookPayload {
  creatorUserSn: number // 크리에이터 사용자 일련번호
  gustSn: number // 크리에이터 방명록 일련번호
}

/**
 * 크리에이터홈 홈탭정보조회
 */
export interface CreatorHome {
  introduce: string
  links: CreatorSocial[]
  notices: CreatorNotice[]
  hotPosts: CreatorContentsItem[]
}

/**
 * 크리에이터홈 홈탭정보조회 페이로드
 */
export interface CreatorHomePayload {
  creatorUserSn: number
}

/**
 * 공지사항 액션
 */
export type NoticeAction =
  | 'modify' // 스크랩 수정
  | 'remove' // 스크랩 삭제

/**
 * 크리에이터 공지사항 리스트 조회 페이로드
 */
export interface CreatorNoticeListPayload {
  creatorUserSn: number // 크리에이터 사용자 일련번호
  pageSize: number // 한 페이지당 row 수
  lastCrtrNttSn?: number // 마지막 크리에이터 공지사항 목록의 crtrNttSn
}

/**
 * 크리에이터 임시저장 공지사항 리스트 조회 페이로드
 */
export interface TempCreatorNoticeListPayload {
  grantType: string
  accessToken: string
}

/**
 * 크리에이터 공지사항 상세 조회 페이로드
 */
export interface CreatorNoticePayload {
  crtrNttSn: number // 공지사항 번호
  creatorUserSn: number // 크리에이터 사용자 일련번호
}

/**
 * 크리에이터 공지사항 등록 페이로드
 */
export interface RegCreatorNoticePayload {
  tempImgUrlList?: string[]
  crtrNttSj: string // 공지사항 제목
  crtrNttCn: string // 공지사항 내용
  tmprNtbdAt: string // 임시저장 여부
}

/**
 * 크리에이터 공지사항 수정 페이로드
 */
export interface UpdateCreatorNoticePayload {
  crtrNttSn: number // 공지사항 번호
  crtrNttSj: string // 공지사항 제목
  crtrNttCn: string // 공지사항 내용
  tmprNtbdAt: string // 임시저장 여부
  fileUrlList?: EditorImage[]
}

/**
 * 크리에이터 공지사항 삭제 페이로드
 */
export interface DeleteCreatorNoticePayload {
  crtrNttSn: number
  creatorUserSn: number // 크리에이터 사용자 일련번호
  grantType: string
  accessToken: string
}

/**
 * 크리에이터설정정보 SNS링크 조회 페이로드
 */
export interface CreatorSnslinkPayload {
  creatorUserSn: number
}

/**
 * 크리에이터 소셜 링크
 */
export interface RegCreatorSocial {
  optionNcm: string // 사용자 정의 소셜 이름
  optionCharstVal: string // 소셜 URL
  expsrOrdr: number // 소셜 리스트 정렬 순서
}

/**
 * 크리에이터 통계 payload
 */
export interface CreatorStatPayload {
  statTyCode: string // 통계검색기준(D-전일/W-전주/M-전)
  statStartDt: string // 통계검색시작일
  statEndDt: string // 통계검색종료일
}

export interface LambdaFileItem {
  fileId: string // 파일ID
  fileNm: string // 파일명
  atchFlpth: string // S3Url
  mediaTyCode?: 'I' | 'V'
}
export interface CreatorShareStatItem {
  cmtyNttSn: number // 커뮤니티 게시글 일련번호
  cmtyNttCtgrySn: number // 커뮤니티 카테고리 일련번호
  userSn: number // 커뮤니티 게시글 작성자
  userAvataUseAt: string // 아바타 여부
  userProfileUrl: string // 커뮤니티 게시글 작성자 프로필
  userNcnm: string // 커뮤니티 게시글 작성자 닉네임
  crtrSpclGradCode: string // 크리에이터 특수 등급 코드
  crtrOrgnztTyCode: CreatorOrganizationCode // 크리에이터 조직 구분 코드
  nttSj: string // 커뮤니티 게시글 제목
  nttCn: string // 커뮤니티 게시글 내용
  nttTyCode: NttTypeCode // 커뮤니티 게시글 유형(NORM:일반형, CARD:카드형, CVOD:영상형)
  rdCount: number // 읽은 수
  shCount: number // 공유 수
  answerCount: number // 댓글 수
  nttRegistDt: string // 등록일자
  imageOrVideoList: LambdaFileItem[] // s3fileList
}

export interface CreatorShareStatPayload {
  lastCmtyNttSn: number // 검색 결과  게시물 일련번호
  statTyCode: string // 통계검색기준(D-전일/W-전주/M-전)
  statStartDt?: string // 통계검색시작일
  statEndDt?: string // 통계검색종료일
  nttTyCode: NttTypeCode // 통계 공유 포스터 유형 ( NORM:게시글/CVOD:영상/CARD:이미지 )
  pageNo: number
  pageSize: number
}

export interface RegCreatorIntroductionPayload {
  optionCharstVal: string
}

/**
 * 크리에이터 프로필
 * TODO: 미사용 인터페이스 확인 후 삭제
 */
// export interface Creator {
//   acntTyCode: UserCodeType // 계정 유형 코드 P(사용자) M(관리자)
//   followerCount: number // 팔로워 수
//   followerYn: BooleanYn // 팔로워 여부 Y/N
//   followingCount: number // 팔로잉 수
//   proflDc: string // 프로필 설명
//   recomendUserId: string // 추천 사용자 ID
//   rwardSmLM: string // 총 보상 LM
//   userAvataUseAt: BooleanYn // 아바타 사용여부 Y/N
//   userNcnm: string // 사용자 닉네임
//   userProflUrl: string // 프로필 사진 url
//   userSn: number // 사용자 일련번호
//   crtrOrgnztTyCode: CreatorOrganizationCode // 크리에이터 조직 구분 코드
// }

/**
 * 크리에이터 신청 요건 충족 정보
 */
export interface ApplyCreatorCondition {
  applicationAvailableAt: BooleanYn // 신청 가능 여부 Y/N
  creatorLink: string | null // 크리에이터 링크
  crtfcProgrsSttusCode: CreatorRegStatus | null // 인증 진행 상태 코드
  resultGradCndTyCode: ApplyCreatorConditionType // 등급 조건 유형 코드
  resultGradCndTyCodeNm: string // 등급 조건 유형 명칭
}

/**
 * 크리에이터 링크 중복체크 페이로드
 */
export interface CreatorLinkCheckDuplicatePayload {
  linkNm: string
}

/**
 * 크리에이터 신청 페이로드: 심사 요건중 하나 이상 충족시
 */
export interface ApplyCreatorSatisfyYesPayload {
  gradCndTyCodeList: ApplyCreatorConditionType[] // 크리에이터 신청 요건 충족 코드 리스트
  referralCode: string // 레퍼럴 코드
}

/**
 * 크리에이터 심사 요청 페이로드: 심사요건 미충족시
 */
export interface ApplyCreatorSatisfyNoPayload {
  referralCode: string // 레퍼럴 코드
}

/**
 * 크리에이터 심사 요청 첨부 콘텐츠 타입
 */
export enum ApplyCreatorAttachContentType {
  LINK = 'CLK',
  FILE = 'CAT',
  SNS = 'SNS',
}

/**
 * 크리에이터 심사 요청 첨부 콘텐츠
 */
export interface ApplyCreatorAttachContent {
  cnvrRequstRegistOrdr: number // 콘텐츠 순서
  crtrCnvrsRequstCn?: string // 콘텐츠 내용 - 컨텐츠링크, SNS주소
  crtrCnvrsRequstFile?: File // 콘텐츠 파일
  crtrCnvrsRequstTyCode: ApplyCreatorAttachContentType // 콘텐츠 타입 - CLK(콘텐츠링크) CAT(파일첨부) SNS(SNS주소)
}

/**
 * 크리에이터 심사 요청 페이로드
 */
export interface ApplyCreatorPayload {
  attachContents: ApplyCreatorAttachContent[] // 콘텐츠 첨부 목록
  creatorLink: string // 크리에이터 링크
  crtrActPlanCn: string // 크리에이터 활동 계획
  crtrCtgrySn: number // 크리에이터 카테고리 일련번호
  requstGradCndTyCode: ApplyCreatorConditionType // 등급 조건 유형 코드 : CUAR(콘텐츠3개이상) CUFW(타플랫폼 기팔로워 1만이상)
  crtrCnvrsCognpathCn?: string // I LIKE LM를 알게 된 경로
}

/**
 * 크리에이터 심사 요청 페이로드 폼데이타
 */
export type ApplyCreatorPayloadFormData = FormData

export type StatisticsOrderBy =
  | 'D' // 어제
  | 'W' // 지난주
  | 'M' // 지난달
  | 'T' // 기간 설정

/**
 * 크리에이터 카테고리 목록 조회 페이로드
 */
export interface CreatorCategoriesPayload {
  crtrOrgnztTyCode: CreatorOrganizationCode // 크리에이터 조직 유형 코드 : IND(개인) BIZ(기업/프로젝트그램)
}

/**
 * 크리에이터 카테고리
 */
export interface CreatorCategory {
  crtrCount: number // 크리에이터 수
  crtrCtgrySn: number // 크리에이터 카테고리 일련번호
  ctgryDc: string // 카테고리 설명
  ctgryNm: string // 카테고리 명
  mainCrtrCount: number //  메인 노출 크리에이터 수
}

/**
 * 크리에이터 목록 조회: 홈 페이로드
 */
export interface CreatorsByHomePayload {
  crtrCtgrySN?: number // 크리에이터 카테고리 일련번호(없으면 전체 카테고리로 조회)
  crtrOrgnztTyCode: CreatorOrganizationCode // 크리에이터 조직 구분 코드
  pageSize: number // 한 페이지당 row 수
}

/**
 * 크리에이터: 홈
 */
export interface CreatorByHome {
  creatorLink: string // 크리에이터 바로가기 링크정보
  creatorNcnm: string // 크리에이터 닉네임
  creatorProfl: string // 크리에이터 프로필 url
  ctgryNm: string // 크리에이터 카테고리명
  userSn: number // 크리에이터 사용자 일련번호
  crtrSpclGradCode: CreatorGrade // 크리에이터 등급
  crtrOrgnztTyCode: CreatorOrganizationCode // 크리에이터 조직 유형 코드
}

/**
 * 크리에이터 목록 조회: 전체 페이로드
 */
export interface CreatorsByAllPayload {
  crtrCtgrySN?: number // 크리에이터 카테고리 일련번호
  crtrOrgnztTyCode: CreatorOrganizationCode // 크리에이터 조직 구분 코드
  pageNum: number // 페이지 번호
  pageSize: number // 한 페이지당 row 수
}

/**
 * 크리에이터: 전체
 */
export interface CreatorByAll extends CreatorByHome {
  proflDc: string // 프로필 설명
}

/**
 * 크리에이터 목록 조회: 노바+ (추천) 페이로드
 */
export interface CreatorsByRecommendedPayload {
  crtrCtgrySN?: number // 크리에이터 카테고리 일련번호
  crtrOrgnztTyCode?: CreatorOrganizationCode // 크리에이터 조직 구분 코드
  pageNum: number // 페이지 번호
  pageSize: number // 한 페이지당 row 수
}

/**
 * 크리에이터: 노바+ (추천)
 */
export interface CreatorByRecommended extends CreatorByHome {
  proflDc: string // 프로필 설명
}

/**
 * 크리에이터 대표 프로필 사진등록/변경
 */
export interface CreatorImageUpdatePayload {
  profileImg: string
}

/**
 * 크리에이터 카테고리 변경
 */
export interface UpdateCreatorCategory {
  crtrCtgrySn: number // 카테고리 일련번호
}

/**
 * 크리에이터 프리미엄 약관 목록 조회
 */
export interface PremiumTermsPayload {
  stplatGroupTyCode: StplatGroupTyCode
}

/**
 * 크리에이터 프리미엄 약관
 */
export interface PremiumTerm {
  crtrStplatId: string // 약관 ID
  stplatEssntlAt: BooleanYn // 약관 필수 여부
  stplatNm: string // 약관명
}
/**
 * 크리에이터 프리미엄 약관 상세 조회 페이로드
 */
export interface PremiumTermPayload {
  crtrStplatId: string // 약관 ID
}

/**
 * 크리에이터 프리미엄 약관 상세 조회
 */
export interface PremiumTermDetail extends PremiumTerm {
  stplatDetailCn: string // 약관 상세 내용
}

/**
 * 크리에이터 프리미엄 상품(구독) 정보 조회
 */
export interface PremiumPayload {
  userSn: number // 크리에이터 사용자 일련번호
}

/**
 * 크리에이터 프리미엄 상품(구독)
 */
export interface Premium {
  crrncySymbolCode: 'KRW' | 'USD' // 화폐 심볼 코드
  pgmSbscrbBeginDt: string // 프로그램 구독 시작 일시 yyyy-MM-dd
  pgmSbscrbEndDt: string // 프로그램 구독 만료 일시 yyyy-MM-dd
  pgmSlePc: number // 프로그램 판매 가격
  pgmSlePcUsd: number // 프로그램 판매 가격 USD
  pgmTaxxptAt: string // 프로그램 면세 여부
  pgmUnitPc: number // 프로그램 단위 가격
  pgmUnitPcUsd: number // 프로그램 단위 가격 USD
  pgmVatPc: number // 프로그램 부가세 가격
  pgmVatPcUsd: number // 프로그램 부가세 가격 USD
  prmbrshImageFileUrl: string // 프리미엄멤버십 이미지 파일 url
  prmbrshPgmDc: string // 프리미엄멤버십 프로그램 설명
  prmbrshPgmNm: string // 프리미엄멤버십 프로그램 명
  prmbrshPgmNoteCn: string // 프리미엄멤버십 프로그램 유의사항 내용
  prmbrshPgmSn: number // 프리미엄멤버십 프로그램 일련번호
  pymntCxTyCode: string // 지불 암호화폐 유형 코드
}

/**
 * 크리에이터 프리미엄 개설(신청) 정보(혜택/유의사항) 조회
 */
export interface PremiumApplication {
  prmbrshDc: string // 프리미엄멤버십 설명(개설 혜택)
  prmbrshNm: string // 프리미엄멤버십 명
  prmbrshNoteCn: string // 프리미엄멤버십 유의사항 내용
}

/**
 * 크리에이터 프리미엄 혜택 목록 조회 페이로드
 */
export interface PremiumBenefitsPayload {
  userSn: number
}

/**
 * 크리에이터 프리미엄 혜택 목록
 */
export type PremiumBenefits = string[]
/**
 * 프리미엄 혜택
 */
export interface PremiumBenefit {
  [Property: string]: string
  prmbrshPgmCn: string // 프리미엄멤버십 프로그램 내용(혜택) 영문
  prmbrshPgmCnKo: string // 프리미엄멤버십 프로그램 내용(혜택) 국문
  prmbrshPgmCnTh: string // 프리미엄멤버십 프로그램 내용(혜택) 태국어
  prmbrshPgmCnEs: string // 프리미엄멤버십 프로그램 내용(혜택) 스페인어
}

/**
 * 프리미엄 혜택 목록 수정
 */
export type UpdatePremiumBenefitsPayload = PremiumBenefit[]

/**
 * 크리에이터 프리미엄 개설(신청)
 */
export interface PremiumApplicationOpenedPayload {
  agreeStplatIdList: string[] // 동의 약관 ID 목록
  pgmSlePc?: number // 프로그램 판매 가격: 사용자 결제할 판매가격
  pgmTaxxptAt?: BooleanYn // 프로그램 면세 여부
  pgmUnitPc?: number // 프로그램 단위 가격
  pgmVatPc?: number // 프로그램 부가세 가격
  prmbrshPgmCnList: PremiumBenefit[] // 프리미엄멤버십 프로그램 내용(혜택) 목록
  prmbrshPgmLevel?: number // 프로그램 레벨
}

/**
 * 크리에이터 프리미엄 후원(구독) 신청 리다이렉트 Params
 */
export interface PremiumDonateRedirectUrlParams {
  creatorUserSn: number // 후원(구독) 신청 완료 후 크리에이터 모델 업데이트를 위한 크리에이터 일련번호
  autoFollow: boolean // 후원(구독) 신청 완료 후 자동 팔로우 여부
}

/**
 * 크리에이터 프리미엄 후원(구독) 신청
 */
export interface PremiumDonatePayload {
  paymentMethod: PayType // 결제 수단
  prmbrshPgmSn: number // 프리미엄멤버십 프로그램 일련번호
  redirectUrl: string // 리다이렉트 받을 URL(PremiumDonateRedirectUrlParams)
  userCmmnSn: number // 사용자 공통 일련번호
}

/**
 * 크리에이터 프리미엄 후원(구독) 신청 > 결제 모듈을 불러오기 위한 URL
 */
export interface PremiumDonate {
  userPrmbrshPgmSbscrbSn: number // 구독하려는 사용자가 멤버쉽 프로그램 가입정보 SN
  merchantOrderNo: string // 주문 일련번호
  url: string // 결제 모듈 호출 URL
}

/**
 * 크리에이터 프리미엄 후원(구독) 결제 완료 여부, 주문 정보 조회 페이로드
 */
export interface PremiumPaymentTradeInfoPayload {
  merchantOrderNo: string // 주문 일련번호
}

/**
 * 크리에이터 프리미엄 후원(구독) 결제 완료 여부, 주문 정보
 */
export interface PremiumPaymentTradeInfo {
  success: boolean
  returnCode: string
  returnMsg: string
  extend: string
  data: {
    amount: string
    fiat: string
    orderNo: string
    payTime: Date
    payType: string
    type: string
    name: string
    uniqueId: string
    appId: string
    merchantOrderNo: string
    message: string
    status:
      | 'NEW' // 주문은 생성됬지만 주문 업데이트가 아직 없으니 폴링해라
      | 'PAY_SUCCESS' // 결제 성공
      | 'PAY_FAIL' // 결제 실패(사유???????)
      | 'TIMEOUT' // 결제 실패(시간 만료 5분)
  } | null
  traceId: string
}

/**
 * 크리에이터 정산 은행 정보 등록 페이로드
 */
export interface CreatorBankPayload {
  bicCode: string // 은행 코드
  bnkbAcnutno: string // 통장 계좌번호
  bnkbDpstrNm: string // 통장 예금주 명
  // 클라이언트에서만 사용하는 모델
  bicBankNm: string // 은행명
}
/**
 * 노바+ 후원자 목록 정렬
 */
export type SupportersOrderBy =
  | 'newest' // 전체 (최신순)
  | 'odrQty' // 후원 횟수(구독 차수)

/**
 * 노바+ 후원자 목록 조회 페이로드
 */
export interface PremiumSupportersPayload {
  orderBy: SupportersOrderBy // 정렬기준
  pageNum: number // 페이지 넘버
  pageSize: number // 페이지당 로우 개수
  searchKeyword?: string // 검색어
}

/**
 * 노바+ 후원자
 */
export interface PremiumSupporter {
  pgmSbscrbBeginDt: string // 프로그램 가입 시작 일시 (추가)
  prmbrshPgmSn: number // 프리미엄멤버십 프로그램 일련번호
  recentSetleSbscrptnOdr: number // 최근 결제 구독 차수
  recentSetleSbscrptnBeginDt: string // 최근 결제 구독 시작 일시 (추가)
  userNcnm: string // 사용자 닉네임
  userPrmbrshPgmSbscrbSn: number // 사용자 프리미엄멤버십 프로그램 가입 일련번호
  userProfileUrl: string // 사용자 프로필 url
  userSn: number // 사용자 일련번호
}

/**
 * 노바+ 후원자 리스폰스
 */
export interface PremiumSupportersRes {
  list: PremiumSupporter[]
  totalCount: number
}

/**
 * 나의 크리에이터
 */
export interface MyCreatorsItem {
  creatorDaoOpenedAt: BooleanYn //  Creator DAO 개설 여부 Y/N
  creatorDaoSbscrbSttusCode: Nullable<CreatorDAOSttusCode> //  Creator DAO 가입 상태 코드 P(가입중) S(가입/획득) T(탈퇴/해제) R(가입거절) B(추방)
  creatorLink: string // 크리에이터 바로가기 링크정보
  creatorNcnm: string // 크리에이터 닉네임
  creatorProfl: string // 크리에이터 프로필 url
  crtrOrgnztTyCode: string // 크리에이터 조직 유형 코드
  crtrSpclGradCode: string // 크리에이터 등급
  ctgryNm: string // 크리에이터 카테고리명
  userSn: number // 크리에이터 사용자 일련번호
  proflDc: string | null // 프로필 설명
}

/**
 * 나의 크리에이터 목록 조회 페이로드
 */
export interface MyCreatorsPayload {
  [key: string]: any
  creatorContentsOrderBy?: BooleanYn //  Creator DAO 개설 여부 Y/N
  crtrCtgrySn?: number // 카테고리 일련번호
  pageNum: number // 요청 페이지 번호
  pageSize: number // 요청 페이지당 갯수
  randSeed?: string // 랜덤 시드
}

/**
 * 나의 크리에이터 목록
 */
export interface MyCreators {
  creatorList: Array<MyCreatorsItem> // 크리에이터 리스트
  randSeed: string // 랜덤 시드
  moreAt: BooleanYn // 더보기 여부
}

/**
 * 크리에이터 목록 아이템
 */
export interface CreatorsItem {
  creatorDaoOpenedAt: BooleanYn //  Creator DAO 개설 여부 Y/N
  creatorDaoSbscrbSttusCode: Nullable<CreatorDAOSttusCode> //  Creator DAO 가입 상태 코드 P(가입중) S(가입/획득) T(탈퇴/해제) R(가입거절) B(추방)
  creatorLink: string // 크리에이터 바로가기 링크정보
  creatorNcnm: string // 크리에이터 닉네임
  creatorProfl: string // 크리에이터 프로필 url
  crtrOrgnztTyCode: string // 크리에이터 조직 유형 코드
  crtrSpclGradCode: string // 크리에이터 등급
  ctgryNm: string // 크리에이터 카테고리명
  userSn: number // 크리에이터 사용자 일련번호
  proflDc: string | null // 프로필 설명
}

/**
 * 크리에이터 목록 조회 페이로드
 */
export interface AllCreatorsPayload {
  [key: string]: any
  creatorDaoOpenedAt?: BooleanYn //  Creator DAO 개설 여부 Y/N
  crtrCtgrySn?: number // 카테고리 일련번호
  pageNum: number // 요청 페이지 번호
  pageSize: number // 요청 페이지당 갯수
  randSeed?: string // 랜덤 시드
}

/**
 * 크리에이터 목록
 */
export interface Creators {
  creatorList: Array<CreatorsItem> // 크리에이터 리스트
  randSeed: string // 랜덤 시드
  moreAt: BooleanYn // 더보기 여부
}

/**
 * 추천 크리에이터 목록 조회 페이로드
 */
export interface RecommendCreatorsPayload {
  [key: string]: any
  crtrCtgrySn?: number // 카테고리 일련번호
  pageNum: number // 요청 페이지 번호
  pageSize: number // 요청 페이지당 갯수
  randSeed?: string // 랜덤 시드
}

/**
 * 추천 크리에이터 목록 조회 응답
 */
export interface RecommendCreators {
  creatorList: Array<CreatorsItem> // 크리에이터 리스트
  randSeed: string // 랜덤 시드
  moreAt: BooleanYn // 더보기 여부
}

export interface WeeklyRewardContents {
  followCount: number
  recommendCount: number
  items: WeeklyRewardItem[]
  payload: WeeklyRewardItemPayload
  refresh: boolean
  last: boolean
}
/**
 * 크리에이터 스토어 스테이트 타입
 */
export interface CreatorState {
  profile: CreatorProfile | null
  myProfile: CreatorProfile | null
  contentsList: BaseInfinityScrollList<
    CreatorContentsItem[],
    CreatorContentsItemListPayload
  >
  postsByHot: BaseInfinityScrollList<
    CreatorContentsItem[],
    CreatorContentsItemListPayload
  >
  contents: CreatorContentsItem | null
  commentContents: BaseInfinityScrollList<
    CreatorCommentItem[],
    CreatorCommentItemListPayload
  >
  rewardContents: BaseInfinityScrollList<RewardItem[], RewardItemPayload>
  rewardCount: RewardCount
  weeklyRewardCount: WeeklyRewardCount
  weeklyRewardContents: WeeklyRewardContents
  rewardChartData: RewardChartData
  rewardActivityChartData: RewardChartActivityData
  creatorGuestBooks: BaseInfinityScrollList<
    CreatorGuestBook[],
    CreatorGuestBooksPayload
  >
  creatorGuestBook: CreatorGuestBook | null
  creatorNoticeList: BaseInfinityScrollList<
    CreatorNotice[],
    CreatorNoticeListPayload
  >
  tempCreatorNoticeList: CreatorNotice[]
  creatorNotice: CreatorNotice | null
  creatorSnsLinks: CreatorSocial[]
  creatorHome: CreatorHome | null
  creatorStatItem: CreatorStatItem
  creatorShareStats: BaseInfinityScrollList<
    CreatorShareStatItem[],
    CreatorShareStatPayload
  >
  commentsAndReplies: BaseInfinityScrollList<
    CommentAndReply[],
    CommentsAndRepliesPayload
  >
  creatorShareVideoStats: BaseInfinityScrollList<
    CreatorShareStatItem[],
    CreatorShareStatPayload
  >
  commentPosts: BaseInfinityScrollList<
    CreatorCommentItem[],
    CreatorCommentItemListPayload
  >
  creatorCategories: CreatorCategory[]
  creatorsByHomePersonal: CreatorByHome[]
  creatorsByHomeProject: CreatorByHome[]
  creatorsByAll: BaseInfinityScrollList<CreatorByAll[], CreatorsByAllPayload>
  creatorsByRecommended: BaseInfinityScrollList<
    CreatorByRecommended[],
    CreatorsByRecommendedPayload
  >
  creatorPremiumSupporters: BasePaginationList<
    PremiumSupporter[],
    PremiumSupportersPayload
  >
  myCreators: BaseInfinityScrollList<MyCreators, MyCreatorsPayload>
  creators: BaseInfinityScrollList<Creators, AllCreatorsPayload>
  recommendCreators: BaseInfinityScrollList<
    RecommendCreators,
    RecommendCreatorsPayload
  >
}
